import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, shareReplay, tap } from 'rxjs';
import { ConfigService } from './config.service';
import { ApiProductCatalogItem, ProductCardView } from '../models/product-api.models';
import { ApiConfigService } from './api-config.service';

@Injectable({ providedIn: 'root' })
export class ProductApiService {
  private cachedList$: Observable<ApiProductCatalogItem[]> | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly apiConfig: ApiConfigService,
    private readonly configService: ConfigService,
  ) {}

  /** GET /api-produit{suffixe}/product-type/findall */
  list(forceRefresh = false): Observable<ApiProductCatalogItem[]> {
    const url = this.apiConfig.productListUrl;

    if (!this.cachedList$ || forceRefresh) {
      this.cachedList$ = this.http.get<ApiProductCatalogItem[]>(url).pipe(
        tap((response) => {
          if (this.configService.enableApiLogs) {
            console.log('[ProductApi] list', url, response);
          }
        }),
        shareReplay(1)
      );
    }

    return this.cachedList$;
  }

  listCards(forceRefresh = false): Observable<ProductCardView[]> {
    return this.list(forceRefresh).pipe(
      map((items) => items.map((item) => this.toCardView(item)))
    );
  }

  getById(id: string): Observable<ApiProductCatalogItem | undefined> {
    return this.list().pipe(
      map((items) => items.find((item) => item.id === id))
    );
  }

  clearCache(): void {
    this.cachedList$ = null;
  }

  toCardView(item: ApiProductCatalogItem): ProductCardView {
    return {
      id: item.id,
      name: item.name,
      description: item.description?.trim() || 'Produit d’assurance Saarvie.',
      imageUrl: this.optimizeImageUrl(item.image?.url, { width: 800, height: 500 }),
      packagesCount: item.pakages?.length ?? 0,
      descriptionsCount: item.descriptions?.length ?? 0,
      nestedProductsCount: item.products?.length ?? 0,
    };
  }

  /**
   * Réduit les images Cloudinary trop lourdes (ex. Malaika ~3 Mo)
   * et remplit le cadre (c_fill).
   */
  optimizeImageUrl(
    url: string | null | undefined,
    opts: { width: number; height: number } = { width: 1200, height: 600 },
  ): string {
    const fallback = 'assets/products/product-auto.jpg';
    if (!url?.trim()) {
      return fallback;
    }

    const transform = `c_fill,g_auto,w_${opts.width},h_${opts.height},f_auto,q_auto`;
    if (url.includes('res.cloudinary.com') && url.includes('/upload/')) {
      return url.replace('/upload/', `/upload/${transform}/`);
    }

    return url;
  }
}
